/**
 * Touch Gesture Hooks
 * Version: 6.2.0
 * 
 * Custom hooks for swipe, tap, long-press, and pull-to-refresh gestures
 */
import { useState, useEffect, useRef, useCallback } from 'react'

// ============================================
// Swipe Detection Hook
// ============================================
interface SwipeState {
  direction: 'left' | 'right' | 'up' | 'down' | null
  distance: number
  startX: number
  startY: number
  isSwiping: boolean
}

interface SwipeHandlers {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  onSwipeUp?: () => void
  onSwipeDown?: () => void
  onSwipeStart?: () => void
  onSwipeEnd?: () => void
}

interface UseSwipeOptions extends SwipeHandlers {
  threshold?: number // Minimum distance to trigger swipe
  timeout?: number // Max duration of swipe
}

function useSwipe(
  elementRef: React.RefObject<HTMLElement>,
  options: UseSwipeOptions = {}
) {
  const { threshold = 50, timeout = 500, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onSwipeStart, onSwipeEnd } = options
  
  const [swipeState, setSwipeState] = useState<SwipeState>({
    direction: null,
    distance: 0,
    startX: 0,
    startY: 0,
    isSwiping: false
  })

  const startTimeRef = useRef<number>(0)
  const isSwipingRef = useRef(false)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0]
    startTimeRef.current = Date.now()
    isSwipingRef.current = true
    
    setSwipeState({
      direction: null,
      distance: 0,
      startX: touch.clientX,
      startY: touch.clientY,
      isSwiping: true
    })
    
    onSwipeStart?.()
  }, [onSwipeStart])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isSwipingRef.current) return
    
    const touch = e.touches[0]
    const deltaX = touch.clientX - swipeState.startX
    const deltaY = touch.clientY - swipeState.startY
    const distance = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY
    
    let direction: 'left' | 'right' | 'up' | 'down' | null = null
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      direction = deltaX > 0 ? 'right' : 'left'
    } else {
      direction = deltaY > 0 ? 'down' : 'up'
    }
    
    setSwipeState(prev => ({
      ...prev,
      direction,
      distance: Math.abs(distance),
      isSwiping: true
    }))
  }, [swipeState.startX, swipeState.startY])

  const handleTouchEnd = useCallback(() => {
    if (!isSwipingRef.current) return
    
    const duration = Date.now() - startTimeRef.current
    
    if (duration <= timeout && swipeState.distance >= threshold) {
      switch (swipeState.direction) {
        case 'left':
          onSwipeLeft?.()
          break
        case 'right':
          onSwipeRight?.()
          break
        case 'up':
          onSwipeUp?.()
          break
        case 'down':
          onSwipeDown?.()
          break
      }
    }
    
    isSwipingRef.current = false
    setSwipeState(prev => ({
      ...prev,
      isSwiping: false
    }))
    
    onSwipeEnd?.()
  }, [swipeState.distance, swipeState.direction, swipeState.isSwiping, threshold, timeout, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onSwipeEnd])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchmove', handleTouchMove, { passive: true })
    element.addEventListener('touchend', handleTouchEnd)
    element.addEventListener('touchcancel', handleTouchEnd)

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
      element.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [elementRef, handleTouchStart, handleTouchMove, handleTouchEnd])

  return swipeState
}

// ============================================
// Long Press Hook
// ============================================
interface UseLongPressOptions {
  onLongPress?: () => void
  onClick?: () => void
  threshold?: number // Time in ms to trigger long press
}

function useLongPress(
  elementRef: React.RefObject<HTMLElement>,
  options: UseLongPressOptions = {}
) {
  const { onLongPress, onClick, threshold = 500 } = options
  
  const [isPressed, setIsPressed] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isLongPressRef = useRef(false)

  const start = useCallback(() => {
    setIsPressed(true)
    isLongPressRef.current = true
    
    timeoutRef.current = setTimeout(() => {
      onLongPress?.()
      isLongPressRef.current = false
    }, threshold)
  }, [onLongPress, threshold])

  const cancel = useCallback(() => {
    setIsPressed(false)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const handleClick = useCallback(() => {
    if (isLongPressRef.current) {
      isLongPressRef.current = false
      return
    }
    onClick?.()
  }, [onClick])

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    element.addEventListener('touchstart', start, { passive: true })
    element.addEventListener('touchend', cancel)
    element.addEventListener('touchcancel', cancel)
    element.addEventListener('click', handleClick)

    return () => {
      element.removeEventListener('touchstart', start)
      element.removeEventListener('touchend', cancel)
      element.removeEventListener('touchcancel', cancel)
      element.removeEventListener('click', handleClick)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [elementRef, start, cancel, handleClick])

  return { isPressed }
}

// ============================================
// Pull to Refresh Hook
// ============================================
interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>
  threshold?: number // Distance to trigger refresh
  resistance?: number // Pull resistance factor
}

function usePullToRefresh(options: UsePullToRefreshOptions) {
  const { onRefresh, threshold = 80, resistance = 2 } = options
  
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isPulling, setIsPulling] = useState(false)
  const startYRef = useRef(0)
  const isPullingRef = useRef(false)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    // Only track if at top of page
    if (window.scrollY === 0) {
      startYRef.current = e.touches[0].clientY
      isPullingRef.current = true
    }
  }, [])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPullingRef.current) return
    
    const currentY = e.touches[0].clientY
    const deltaY = currentY - startYRef.current
    
    // Only allow pulling down
    if (deltaY > 0) {
      const resistanceFactor = Math.min(deltaY / resistance, deltaY)
      setPullDistance(resistanceFactor)
      setIsPulling(resistanceFactor > 10)
    }
  }, [resistance])

  const handleTouchEnd = useCallback(async () => {
    if (isPullingRef.current && pullDistance >= threshold) {
      setIsRefreshing(true)
      setPullDistance(0)
      setIsPulling(false)
      
      await onRefresh()
      
      setIsRefreshing(false)
    } else {
      setPullDistance(0)
      setIsPulling(false)
    }
    
    isPullingRef.current = false
  }, [pullDistance, threshold, onRefresh])

  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: true })
    document.addEventListener('touchend', handleTouchEnd)
    document.addEventListener('touchcancel', handleTouchEnd)

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  return {
    pullDistance,
    isRefreshing,
    isPulling,
    progress: Math.min(pullDistance / threshold, 1)
  }
}

// ============================================
// Double Tap Hook
// ============================================
interface UseDoubleTapOptions {
  onDoubleTap?: () => void
  delay?: number // Max time between taps
}

function useDoubleTap(
  elementRef: React.RefObject<HTMLElement>,
  options: UseDoubleTapOptions = {}
) {
  const { onDoubleTap, delay = 300 } = options
  
  const lastTapRef = useRef<number>(0)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const handleClick = () => {
      const now = Date.now()
      if (now - lastTapRef.current < delay) {
        onDoubleTap?.()
        lastTapRef.current = 0
      } else {
        lastTapRef.current = now
      }
    }

    element.addEventListener('click', handleClick)
    return () => element.removeEventListener('click', handleClick)
  }, [elementRef, onDoubleTap, delay])
}

// ============================================
// Pinch to Zoom Hook
// ============================================
interface UsePinchToZoomOptions {
  onZoom?: (scale: number) => void
  minScale?: number
  maxScale?: number
}

function usePinchToZoom(
  elementRef: React.RefObject<HTMLElement>,
  options: UsePinchToZoomOptions = {}
) {
  const { onZoom, minScale = 1, maxScale = 3 } = options
  
  const initialDistanceRef = useRef<number>(0)
  const currentScaleRef = useRef<number>(1)

  const getDistance = (touches: TouchList) => {
    return Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    )
  }

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        initialDistanceRef.current = getDistance(e.touches)
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const currentDistance = getDistance(e.touches)
        const scale = currentDistance / initialDistanceRef.current
        const newScale = Math.min(Math.max(scale * currentScaleRef.current, minScale), maxScale)
        
        currentScaleRef.current = newScale
        onZoom?.(newScale)
      }
    }

    const handleTouchEnd = () => {
      initialDistanceRef.current = 0
    }

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchmove', handleTouchMove, { passive: true })
    element.addEventListener('touchend', handleTouchEnd)

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
    }
  }, [elementRef, onZoom, minScale, maxScale])
}

// ============================================
// Export All Hooks
// ============================================
export {
  useSwipe,
  useLongPress,
  usePullToRefresh,
  useDoubleTap,
  usePinchToZoom
}

export type { SwipeState, SwipeHandlers, UseSwipeOptions, UseLongPressOptions, UsePullToRefreshOptions, UseDoubleTapOptions, UsePinchToZoomOptions }